/**
 * ResultsTab Component
 *
 * Displays results table for a batch with:
 * - Loading spinner while fetching
 * - Error state with retry button
 * - Empty state message
 * - ResultsTable component with all results
 * - Status filter dropdown
 * - Pagination
 */

import React, { useState } from 'react';
import { useResults } from '../../hooks/useResults';
import { useProject } from '../../hooks/useProjects';
import { useBatch } from '../../hooks/useBatches';
import { ResultsTable } from './ResultsTable';
import { ResultStatus } from '../../types/result';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { resultService } from '../../services/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface ResultsTabProps {
  batchId: string;
}

const STATUS_FILTER_OPTIONS: Array<{ value: '' | ResultStatus; label: string }> = [
  { value: '', label: 'All Statuses' },
  { value: 'none', label: 'Not Executed' },
  { value: 'error', label: 'Error' },
  { value: 'empty', label: 'Incomplete' },
  { value: 'complete', label: 'Complete' },
];

export const ResultsTab: React.FC<ResultsTabProps> = ({ batchId }) => {
  const [statusFilters, setStatusFilters] = useState<ResultStatus[]>([]);
  const [errorFilter, setErrorFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const limit = 50;
  const queryClient = useQueryClient();

  // Fetch batch to get project_id
  const { data: batch } = useBatch(batchId);

  // Fetch project to get critical_keys
  const { data: project } = useProject(batch?.project_id);
  const criticalKeys = project?.critical_keys || [];

  // Fetch results with filters
  const { data, isLoading, error, refetch } = useResults(batchId, {
    status: statusFilters.length === 1 ? statusFilters[0] : undefined,
    sort_by: 'timestamp',
    sort_order: 'desc',
    limit,
    offset: page * limit,
  });

  // Fetch counts for each selected status to get total across all pages (only when multiple selected)
  const needMultipleCounts = statusFilters.length > 1;
  const errorCounts = useResults(batchId, {
    status: 'error',
    limit: 1,
  });
  const emptyCounts = useResults(batchId, {
    status: 'empty',
    limit: 1,
  });
  const completeCounts = useResults(batchId, {
    status: 'complete',
    limit: 1,
  });
  const noneCounts = useResults(batchId, {
    status: 'none',
    limit: 1,
  });

  // Client-side filter for multiple statuses and error messages
  const filteredResults = data?.results?.filter(result => {
    // Status filter
    if (statusFilters.length > 0 && !statusFilters.includes(result.status)) {
      return false;
    }
    // Error message filter
    if (errorFilter && (!result.error || !result.error.toLowerCase().includes(errorFilter.toLowerCase()))) {
      return false;
    }
    return true;
  }) || [];

  const toggleStatusFilter = (status: ResultStatus) => {
    setStatusFilters(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setPage(0);
  };

  // Calculate total selected count across all pages
  let totalSelectedCount = 0;
  if (statusFilters.length === 1) {
    // Single status - use data.total from main query
    totalSelectedCount = data?.total || 0;
  } else if (statusFilters.length > 1) {
    // Multiple statuses - sum up counts for each
    if (statusFilters.includes('error')) totalSelectedCount += errorCounts.data?.total || 0;
    if (statusFilters.includes('empty')) totalSelectedCount += emptyCounts.data?.total || 0;
    if (statusFilters.includes('complete')) totalSelectedCount += completeCounts.data?.total || 0;
    if (statusFilters.includes('none')) totalSelectedCount += noneCounts.data?.total || 0;
  } else if (errorFilter && statusFilters.length === 0) {
    // Error filter only - need to fetch all to count, show warning
    totalSelectedCount = data?.total || 0; // This is approximate
  }

  // Filters determine selection
  const hasActiveFilters = statusFilters.length > 0 || errorFilter.length > 0;
  const selectedCount = hasActiveFilters ? totalSelectedCount : 0;

  const handleRerunSelected = async () => {
    if (selectedCount === 0) return;

    try {
      // Fetch all results matching filters to get all job_ids
      let allJobIds: string[] = [];

      if (statusFilters.length > 0) {
        for (const status of statusFilters) {
          let offset = 0;
          const fetchLimit = 1000;
          while (true) {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/api/results?batch_id=${batchId}&status=${status}&limit=${fetchLimit}&offset=${offset}`
            );
            if (!response.ok) throw new Error('Failed to fetch results');
            const data = await response.json();

            // Apply error filter client-side if needed
            const filtered = errorFilter
              ? data.results.filter((r: any) => r.error && r.error.toLowerCase().includes(errorFilter.toLowerCase()))
              : data.results;

            allJobIds = allJobIds.concat(filtered.map((r: any) => r.job_id));
            if (data.results.length < fetchLimit) break;
            offset += fetchLimit;
          }
        }
      }

      // Call batch execute API with specific job_ids
      const response = await authenticatedFetch(`${API_BASE_URL}/api/batches/${batchId}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_ids: allJobIds })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to execute jobs');
      }

      const result = await response.json();
      toast.success(`Execution started! ${result.jobs_queued} jobs queued for execution.`);
      refetch(); // Refresh results
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to execute jobs');
    }
  };

  const handleExportOutputs = async () => {
    try {
      // Fetch all results matching current filters (across all pages)
      let allResults: any[] = [];
      let offset = 0;
      const fetchLimit = 1000;

      // Fetch all results for selected statuses
      if (statusFilters.length > 0) {
        for (const status of statusFilters) {
          offset = 0;
          while (true) {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/api/results?batch_id=${batchId}&status=${status}&limit=${fetchLimit}&offset=${offset}&sort_by=timestamp&sort_order=desc`
            );
            if (!response.ok) throw new Error('Failed to fetch results');
            const data = await response.json();
            allResults = allResults.concat(data.results);
            if (data.results.length < fetchLimit) break;
            offset += fetchLimit;
          }
        }
      } else {
        // Fetch all results (no status filter)
        while (true) {
          const response = await authenticatedFetch(
            `${API_BASE_URL}/api/results?batch_id=${batchId}&limit=${fetchLimit}&offset=${offset}&sort_by=timestamp&sort_order=desc`
          );
          if (!response.ok) throw new Error('Failed to fetch results');
          const data = await response.json();
          allResults = allResults.concat(data.results);
          if (data.results.length < fetchLimit) break;
          offset += fetchLimit;
        }
      }

      // Client-side filter by error message if needed
      if (errorFilter) {
        allResults = allResults.filter(r =>
          r.error && r.error.toLowerCase().includes(errorFilter.toLowerCase())
        );
      }

      // Build output object with input_id as keys
      const outputsObject: Record<string, any> = {};

      for (const result of allResults) {
        const inputId = result.input_id || result.job_id;
        if (result.result) {
          outputsObject[inputId] = result.result;
        }
      }

      // Export as JSON file
      const jsonString = JSON.stringify(outputsObject, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filterSuffix = statusFilters.length > 0 ? `-${statusFilters.join('-')}` : '';
      a.download = `batch-${batchId}-outputs${filterSuffix}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${Object.keys(outputsObject).length} outputs`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export outputs');
    }
  };

  // Recheck results mutation
  const recheckResultsMutation = useMutation({
    mutationFn: () => resultService.recheck(batchId),
    onSuccess: (result) => {
      const message = result.status_changes > 0
        ? `Rechecked ${result.total_rechecked} results. ${result.status_changes} status changes: ${result.new_complete} complete, ${result.new_empty} incomplete, ${result.new_error} errors.`
        : `Rechecked ${result.total_rechecked} results. No status changes.`;
      toast.success(message);
      // Invalidate all result queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['results', batchId] });
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to recheck results');
    },
  });

  // Reset none results mutation
  const [showResetNoneConfirm, setShowResetNoneConfirm] = useState(false);
  const resetNoneMutation = useMutation({
    mutationFn: () => resultService.resetNone(batchId),
    onSuccess: (result) => {
      toast.success(result.message);
      setShowResetNoneConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['results', batchId] });
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reset none results');
      setShowResetNoneConfirm(false);
    },
  });

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading results</h3>
            <p className="mt-1 text-sm text-red-700">{error.message}</p>
            <div className="mt-4">
              <button
                onClick={() => refetch()}
                className="text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-2 py-1"
                aria-label="Retry loading results"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
          aria-label="Loading results"
        ></div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const totalPages = Math.ceil(data.total / limit);

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      {hasActiveFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-blue-900">
                {selectedCount} result(s) selected across all pages
              </span>
              <p className="text-xs text-blue-700 mt-1">
                {statusFilters.length > 0 && `Status: ${statusFilters.join(', ')}`}
                {statusFilters.length > 0 && errorFilter && ' | '}
                {errorFilter && `Error contains: "${errorFilter}"`}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => recheckResultsMutation.mutate()}
                disabled={recheckResultsMutation.isPending}
                className="px-4 py-2 bg-white text-purple-600 text-sm font-medium rounded-md border border-purple-300 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Revalidate all results based on current critical keys"
              >
                {recheckResultsMutation.isPending ? 'Checking...' : 'Check Results'}
              </button>
              <button
                onClick={() => setShowResetNoneConfirm(true)}
                disabled={resetNoneMutation.isPending}
                className="px-4 py-2 bg-white text-orange-600 text-sm font-medium rounded-md border border-orange-300 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reset all 'Not Executed' results"
              >
                {resetNoneMutation.isPending ? 'Resetting...' : 'Reset Not Executed'}
              </button>
              <button
                onClick={handleExportOutputs}
                disabled={selectedCount === 0}
                className="px-4 py-2 bg-white text-blue-600 text-sm font-medium rounded-md border border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Export Outputs
              </button>
              <button
                onClick={handleRerunSelected}
                disabled={selectedCount === 0}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Execute {selectedCount} Job{selectedCount !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <div className="space-y-2">
              {STATUS_FILTER_OPTIONS.slice(1).map((option) => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilters.includes(option.value as ResultStatus)}
                    onChange={(e) => toggleStatusFilter(option.value as ResultStatus)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="error-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Error Message
            </label>
            <div className="relative">
              <input
                type="text"
                id="error-filter"
                value={errorFilter}
                onChange={(e) => {
                  setErrorFilter(e.target.value);
                  setPage(0); // Reset to first page when filter changes
                }}
                placeholder="Search error messages..."
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pr-8"
              />
              {errorFilter && (
                <button
                  onClick={() => {
                    setErrorFilter('');
                    setPage(0);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Clear error filter"
                  title="Clear filter"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div className="text-sm text-gray-500 self-end pb-2">
            Showing {filteredResults.length} of {selectedCount > 0 ? selectedCount : data?.total || 0} results
          </div>
        </div>
      </div>

      {/* Results Table */}
      {filteredResults.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No results found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {statusFilters.length > 0 || errorFilter
              ? 'No results match the selected filters'
              : 'Results will appear here as jobs complete execution'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <ResultsTable
            results={filteredResults}
            criticalKeys={criticalKeys}
            onErrorClick={(errorMessage) => {
              setErrorFilter(errorMessage);
              setPage(0);
            }}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{page * limit + 1}</span> to{' '}
                    <span className="font-medium">{Math.min((page + 1) * limit, data.total)}</span> of{' '}
                    <span className="font-medium">{data.total}</span> results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Previous page"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {page + 1} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Next page"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reset None Results Confirmation Dialog */}
      {showResetNoneConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Reset Not Executed Results?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                This will delete all results with status "Not Executed" and reset the corresponding jobs back to "pending".
                This helps clean up results that were created but never actually executed.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowResetNoneConfirm(false)}
                  disabled={resetNoneMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => resetNoneMutation.mutate()}
                  disabled={resetNoneMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                >
                  {resetNoneMutation.isPending ? 'Resetting...' : 'Reset Results'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
